import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  HomeIcon,
  DocumentTextIcon,
  PlusIcon,
  UserIcon,
  UsersIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ShoppingBagIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "../../contexts/TranslationContext";
import LanguageSwitcher from "../common/LanguageSwitcher";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();

  const navigation = [
    {
      name: t("layout.navigation.dashboard"),
      href: "/dashboard",
      icon: HomeIcon,
    },
    {
      name: t("layout.navigation.forms"),
      href: "/forms",
      icon: DocumentTextIcon,
    },
    { name: t("forms.list.createForm"), href: "/forms/new", icon: PlusIcon },
    ...(user?.role === "admin"
      ? [
          {
            name: t("layout.navigation.woocommerce"),
            href: "/woocommerce/orders",
            icon: ShoppingBagIcon,
          },
        ]
      : []),
  ];

  // Add user management for admins
  const userNavigation = [
    { name: "My Profile", href: "/profile", icon: CogIcon },
    ...(user?.role === "admin"
      ? [{ name: "User Management", href: "/users", icon: UsersIcon }]
      : []),
  ];

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 bg-primary-600">
            <h1 className="text-xl font-bold text-white">
              tato.net integrator
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {/* Main Navigation */}
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive(item.href)
                      ? "bg-primary-100 text-primary-700"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}

            {/* Divider */}
            <div className="border-t border-gray-200 my-4"></div>

            {/* User Navigation */}
            {userNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive(item.href)
                      ? "bg-primary-100 text-primary-700"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Language Switcher */}
          <div className="px-4 py-3 border-t border-gray-100">
            <div className="flex items-center justify-center">
              <LanguageSwitcher />
            </div>
          </div>

          {/* User menu */}
          <div className="px-4 py-4 border-t border-gray-200">
            <div className="flex items-center">
              <UserIcon className="w-8 h-8 text-gray-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">
                  {user?.username}
                </p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center w-full px-4 py-2 mt-2 text-sm text-gray-600 rounded-md hover:bg-gray-100 hover:text-gray-900"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
              {t("layout.navigation.logout")}
            </button>
          </div>

          {/* Version */}
          <div className="px-2 py-2 border-t border-gray-200 flex items-center justify-center">
            <div className="flex items-center">
              <p className="text-xs text-gray-500">
                {t("layout.navigation.version") +
                  " " +
                  process.env.REACT_APP_VERSION}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="pl-64">
        <main className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
